import React from 'react';
import { Eye, FileText, Palette, Download, Pin, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { getProductHeroImage } from '../../data/productHeroImages';
import { demoPages } from '../../data/demoPages';
import { getCategoryIdentity } from '../../data/productVisualSystem';
import FavoriteButton from '../FavoriteButton';
import AddToCollectionButton from '../AddToCollectionButton';
import { openPinterestStudio } from '../../utils/pinterestStudioHelper';

export const ProductCard = ({ title, sub, label, labelColor, colors, type, pageCount, price, worth, onClick, category, slug, priceUsd, personalPriceUsd, commercialPriceUsd }) => {
  const navigate = useNavigate();
  const heroImg = getProductHeroImage(slug || title.toLowerCase().replace(/\s+/g, '-'));
  const demo = demoPages[title];
  const identity = getCategoryIdentity(category || type);

  return (
    <div data-testid={`product-card-${slug || title.toLowerCase().replace(/\s+/g, '-')}`}
      className="group relative rounded-xl overflow-hidden bg-white border border-[#DDD4C8]/60 shadow-[0_2px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(74,53,37,0.10)] hover:-translate-y-2 transition-all duration-500 cursor-pointer flex flex-col"
      onClick={onClick}>
      {/* Label badge */}
      {label && (
        <div className={`absolute top-4 left-4 z-20 text-[9px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full shadow-sm ${labelColor}`}>
          {label}
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5">
        <AddToCollectionButton productTitle={title} size="sm" />
        <FavoriteButton productTitle={title} size="sm" />
      </div>

      {/* Hero Image Area */}
      <div className="relative h-[300px] md:h-[320px] overflow-hidden">
        {heroImg ? (
          <>
            <img
              src={heroImg.url}
              alt={heroImg.alt}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              style={{ objectPosition: heroImg.focal || 'center' }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />
          </>
        ) : demo?.pages[0] ? (
          <div
            className="w-full h-full px-8 pt-8 pb-6 flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${identity.bg}, ${colors?.bg || identity.bg})` }}
          >
            <div className="relative w-full max-w-[220px]">
              <div className="absolute inset-0 rounded-lg bg-white/40 shadow-sm" style={{ transform: 'rotate(3deg) translateX(6px) translateY(-4px) scale(0.94)' }} />
              <div className="absolute inset-0 rounded-lg bg-white/60 shadow-md" style={{ transform: 'rotate(1.5deg) translateX(3px) translateY(-2px) scale(0.97)' }} />
              <div className="relative bg-white rounded-lg shadow-lg overflow-hidden aspect-[3/4]">
                {demo.pages[0]({ accent: colors?.accent || identity.accent })}
              </div>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: identity.bg }}>
            <span className="font-serif text-4xl font-bold" style={{ color: identity.accent + '40' }}>{title[0]}</span>
          </div>
        )}

        {/* Category pill */}
        <div
          className="absolute bottom-4 left-4 text-[10px] font-semibold uppercase tracking-[0.12em] px-3 py-1 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-sm z-10"
          style={{ color: identity.pillText }}
        >
          {category || type}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out pointer-events-none z-[5]">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg px-5 py-2.5 shadow-xl flex items-center gap-2 scale-90 group-hover:scale-100 transition-transform duration-300">
            <Eye className="w-4 h-4 text-[#4A3525]" />
            <span className="text-xs font-bold text-[#4A3525] tracking-wide">Preview</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 md:p-6 flex flex-col flex-1">
        {/* Category accent strip */}
        <div className="w-8 h-[2px] rounded-full mb-3" style={{ backgroundColor: identity.accent + '60' }} />

        <div className="flex items-start justify-between mb-1">
          <h3
            className="font-bold text-[#4A3525] text-base md:text-lg leading-snug tracking-tight group-hover:text-[#B49A88] transition-colors duration-300"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h3>
          {price && <span className="text-sm font-bold text-[#4A3525] flex-shrink-0 ml-3" style={{ fontFamily: "'Playfair Display', serif" }}>{price}</span>}
        </div>
        <p className="text-xs text-[#8A7D72] mb-3">{sub}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          <span className="text-[10px] font-sans text-[#8A7D72] bg-[#F7F2EC] px-2.5 py-1 rounded-full flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> {pageCount}</span>
          <span className="text-[10px] font-sans text-[#8A7D72] bg-[#F7F2EC] px-2.5 py-1 rounded-full flex items-center gap-1"><Palette className="w-2.5 h-2.5" /> Canva editable</span>
          <span className="text-[10px] font-sans text-[#8A7D72] bg-[#F7F2EC] px-2.5 py-1 rounded-full flex items-center gap-1"><Download className="w-2.5 h-2.5" /> Instant download</span>
          {commercialPriceUsd && (
            <span data-testid="license-badge" className="text-[10px] font-sans text-[#8B7B6B] bg-[#F5F0EB]/90 border border-[#E8DFC8]/60 px-2.5 py-1 rounded-full flex items-center gap-1">
              <Shield className="w-2.5 h-2.5" /> Commercial License available
            </span>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2 mt-auto">
          <Button data-testid={`preview-btn-${slug || title.toLowerCase().replace(/\s+/g, '-')}`}
            variant="outline"
            className="flex-1 rounded-full py-2.5 text-xs font-semibold border-[#DDD4C8] text-[#8A7D72] hover:bg-[#F7F2EC] hover:text-[#4A3525] transition-all duration-300 flex items-center justify-center gap-1.5"
            onClick={(e) => { e.stopPropagation(); onClick(); }}>
            <Eye className="w-3.5 h-3.5" /> Preview
          </Button>
          <button
            data-testid={`pinterest-btn-${slug || title.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={(e) => {
              e.stopPropagation();
              openPinterestStudio(navigate, { title, description: sub, category: category || type });
            }}
            className="w-10 h-10 rounded-full border border-[#DDD4C8] flex items-center justify-center text-[#A89B8E] hover:text-[#E60023] hover:border-[#E60023]/30 hover:bg-[#E60023]/5 transition-all"
            title="Promote on Pinterest"
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
